import { inject } from '@angular/core';
import { isHttpStatusError } from '@mkp/shared/data-access';
import { inviteApiActions } from '@mkp/account-membership/actions';
import { InviteResource } from '@mkp/account-membership/data-access';
import { Invite } from '@mkp/account-membership/models';
import { editInviteActions, invitedUsersActions } from '@mkp/user-account-membership/actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

export const deleteInvite = createEffect(
  (actions$ = inject(Actions), inviteResource = inject(InviteResource)) => {
    return actions$.pipe(
      ofType(editInviteActions.deleteInvite, invitedUsersActions.deleteInvite),
      switchMap(({ inviteId, page }) =>
        inviteResource.delete(inviteId).pipe(
          map(() => inviteApiActions.deleteSuccess({ id: inviteId, page })),
          catchError((error: unknown) => {
            const status = isHttpStatusError(error) ? error.status : 500;
            return of(inviteApiActions.deleteFailure({ status, id: inviteId, page }));
          })
        )
      )
    );
  },
  { functional: true }
);

export const updateInvite = createEffect(
  (actions$ = inject(Actions), inviteResource = inject(InviteResource)) => {
    return actions$.pipe(
      ofType(editInviteActions.updateInvite),
      switchMap(({ invite, roles }) =>
        inviteResource.update(invite.id, { roles: roles, _version: invite._version }).pipe(
          map(() => inviteApiActions.updateInviteSuccess()),
          catchError((error: unknown) => {
            const status = isHttpStatusError(error) ? error.status : 500;
            return of(inviteApiActions.updateInviteFailure({ status }));
          })
        )
      )
    );
  },
  { functional: true }
);
export const deleteBeforeResendInvite = createEffect(
  (actions$ = inject(Actions), inviteResource = inject(InviteResource)) => {
    return actions$.pipe(
      ofType(editInviteActions.resendInvite, invitedUsersActions.resendInvite),
      switchMap(({ invite, page }) =>
        inviteResource.delete(invite.id).pipe(
          map(() => inviteApiActions.deleteBeforeResendSuccess({ invite, page })),
          catchError((error: unknown) => {
            const status = isHttpStatusError(error) ? error.status : 500;
            const action =
              status === 404
                ? inviteApiActions.deleteBeforeResendNotFound({ invite, page }) // delete not-found is same as success
                : inviteApiActions.deleteBeforeResendFailure();
            return of(action);
          })
        )
      )
    );
  },
  { functional: true }
);

export const resendInvite = createEffect(
  (actions$ = inject(Actions), inviteResource = inject(InviteResource)) => {
    return actions$.pipe(
      ofType(
        inviteApiActions.deleteBeforeResendSuccess,
        inviteApiActions.deleteBeforeResendNotFound
      ),
      switchMap(({ invite: { accountId, email, roles }, page }) =>
        // TODO - replace with InvitePost when it's merged
        inviteResource.add({ accountId, email, roles } as Invite).pipe(
          map((newInvite) => inviteApiActions.resendSuccess({ invite: newInvite, page })),
          catchError(() => of(inviteApiActions.resendFailure({ page })))
        )
      )
    );
  },
  { functional: true }
);
